import { apiErrorMessage } from '../utils/apiError';
import { authenticatedFetch } from './api_utils';
import { functionEndpoint } from './endpoints';

const ENDPOINT = functionEndpoint('delete_resource');

// The preview: what the deletion would take with it, whether anything blocks
// it, and a fingerprint of that scope which the execute must hand back.
export async function previewResourceDelete(resourceType, resourceId, extra = {}) {
  const response = await authenticatedFetch(ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      resource_type: resourceType,
      resource_id: resourceId,
      ...extra,
      mode: 'preview',
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message = apiErrorMessage(body, `Could not check deletion dependencies (HTTP ${response.status}).`);
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

const PARTIAL_DELETE_ITEMS = {
  function: 'automations, view cards or stored Function files',
  view: 'automations',
  tag_category: 'profile defaults or category values',
};

function incompleteDeletionError(resourceType) {
  const items = PARTIAL_DELETE_ITEMS[resourceType];
  const error = new Error(`Deletion could not be completed. Some associated ${items} may already have been removed. Retry deletion to finish cleanup.`);
  error.deletionIncomplete = true;
  return error;
}

// `preview` is what previewResourceDelete returned for this target (null for a
// resource that is deleted without one). The backend collects the dependents
// again and refuses when they no longer match the preview the user confirmed:
// that refusal is thrown with `scopeChanged` and the fresh preview, for a
// fresh confirmation. A refusal because something blocks the deletion is
// thrown with `blocked` and the dependents, as before.
export async function executeResourceDelete(resourceType, resourceId, preview, extra = {}) {
  let response;
  try {
    response = await authenticatedFetch(ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        resource_type: resourceType,
        resource_id: resourceId,
        ...extra,
        scope_fingerprint: preview?.scope_fingerprint,
        mode: 'execute',
      }),
    });
  } catch (error) {
    // A lost reply does not establish whether the server removed anything.
    if (PARTIAL_DELETE_ITEMS[resourceType]) throw incompleteDeletionError(resourceType);
    throw error;
  }
  const body = await response.json().catch(() => null);
  if (PARTIAL_DELETE_ITEMS[resourceType]
      && (body?.deletion_incomplete || (!body && response.status >= 500))) {
    throw incompleteDeletionError(resourceType);
  }
  if (response.status === 409 && body?.scope_changed) {
    const error = new Error('scope changed');
    error.scopeChanged = true;
    error.preview = {
      blocking: body.blocking,
      dependents: body.dependents,
      scope_fingerprint: body.scope_fingerprint,
    };
    throw error;
  }
  if (response.status === 409) {
    const error = new Error(apiErrorMessage(body, 'Deletion is blocked.'));
    error.blocked = true;
    error.dependents = body?.dependents;
    throw error;
  }
  if (!response.ok) {
    const error = new Error(apiErrorMessage(body, `Delete failed (HTTP ${response.status}).`));
    error.status = response.status;
    throw error;
  }
  if (body?.deleted !== true) {
    if (PARTIAL_DELETE_ITEMS[resourceType]) throw incompleteDeletionError(resourceType);
    throw new Error('The server did not confirm deletion. Please check the resource before retrying.');
  }
  return body;
}
